import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Hero from "../components/Hero";
import SectionTitle from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import GalleryCard from "../components/GalleryCard";
import Timeline from "../components/Timeline";
import Stats from "../components/Stats";
import CTA from "../components/CTA";
import Button from "../components/Button";
import { TestimonialSlider } from "../components/TestimonialCard";
import {
  FloatingFurniture,
  Sawdust,
  WoodRings,
  DovetailDivider,
  PendulumLamp,
  SET_A,
  SET_B,
} from "../components/Decor";
import useSmoothScroll from "../hooks/useSmoothScroll";
import { SITE } from "../data/siteConfig";
import {
  REACH,
  SERVICES,
  GALLERY_ITEMS,
  TESTIMONIALS,
  PROCESS_STEPS,
  ICONS,
} from "../data/content";
import "../css/Home.css";
import "../css/Homefx.css";
import "../css/HomeDecor.css";
import "../css/Joinery.css";
import IndiaReachMap from "../components/IndiaReachMap";

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
  {
    icon: ICONS.experience,
    title: "35+ Years Experience",
    description: "Over three decades of trusted craftsmanship, since 1990.",
  },
  {
    icon: ICONS.design,
    title: "Everything Customised",
    description:
      "Wardrobes, kitchens, TV units, panelling, stairs, all made to your space.",
  },
  {
    icon: ICONS.wood,
    title: "Premium Wood",
    description:
      "Seasoned timber chosen for strength, finish and lasting quality.",
  },
  {
    icon: ICONS.delivery,
    title: "Orders From All Over India",
    description:
      "Based in Palakkad, building and delivering across the country.",
  },
];

const MARQUEE = [
  "Wardrobes",
  "Kitchen Units",
  "TV Units",
  "Panel Works",
  "Wooden Stairs",
  "Pan-India Orders",
];

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1581428982868-e410dd047a90?auto=format&fit=crop&w=900&q=80";

// (was named `useFallback`, but it isn't a hook, so the rule-of-hooks linter
// would complain when used inside JSX handlers)
const handleImgError = (e) => {
  if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG;
};

const JOINERY_IMAGES = {
  main: {
    src: "/assets/114.png",
    alt: "Craftsman shaping a precise wooden joint by hand",
  },
  timber: {
    src: "/assets/31.png",
    alt: "Carefully selected seasoned timber planks",
  },
  detail: {
    src: "/assets/32.png",
    alt: "Close-up of a traditional interlocking wood joint",
  },
};

function FeatureCard({ icon, title, description }) {
  return (
    <div className="feature-card fx-feature">
      <div className="feature-card__icon">{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

function Marquee() {
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee__track">
        {items.map((word, i) => (
          <span key={`${word}-${i}`} className={i % 2 ? "is-outline" : ""}>
            {word}
            <i />
          </span>
        ))}
      </div>
    </div>
  );
}

function AboutPreview() {
  return (
    <section className="section about-preview decor-host">
      <div className="decor-grain" aria-hidden="true" />
      <WoodRings />
      <div className="container about-preview__grid">
        <div className="about-preview__media fx-clip">
          <img
            data-parallax="0.12"
            src="/annalogo.png"
            alt="Craftsman finishing a wooden furniture piece at Anappuram workshop"
            loading="lazy"
          />
          <div className="about-preview__badge">
            <strong>37+</strong>
            <span>Years of Craftsmanship</span>
          </div>
        </div>
        <div className="about-preview__content" data-reveal="right">
          <SectionTitle
            eyebrow="Our Story"
            title="Handcrafted Furniture Rooted in Kerala Tradition"
          />
          <p>
            Since 1990, Anappuram Wooden Furniture Work has been building custom
            furniture from our workshop in Palakkad. From wardrobes, kitchen
            units and TV units to interior panel works and staircases,
            everything is customised to your space.
          </p>
          <p>
            Our work isn't limited to Kerala. We take orders from all over the
            country.
          </p>
          <Button to="/about" variant="outline">
            Read More
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Mula Yani Joinery ---------- */
function JoinerySection() {
  return (
    <section className="section joinery">
      <span className="joinery__bigword" data-drift="-14" aria-hidden="true">
        Mula Yani · Mula Yani
      </span>

      <div className="container">
        <div className="joinery__grid">
          {/* Image collage */}
          <div className="joinery__collage" data-reveal="left">
            <div className="joinery__main fx-clip">
              <img
                data-parallax="0.1"
                src={JOINERY_IMAGES.main.src}
                alt={JOINERY_IMAGES.main.alt}
                loading="lazy"
                onError={handleImgError}
              />
            </div>

            <div className="joinery__float joinery__float--timber">
              <img
                data-parallax="0.08"
                src={JOINERY_IMAGES.timber.src}
                alt={JOINERY_IMAGES.timber.alt}
                loading="lazy"
                onError={handleImgError}
              />
              <span>Selected Timber</span>
            </div>

            <div className="joinery__float joinery__float--detail">
              <img
                data-parallax="0.08"
                src={JOINERY_IMAGES.detail.src}
                alt={JOINERY_IMAGES.detail.alt}
                loading="lazy"
                onError={handleImgError}
              />
              <span>Precision Joint</span>
            </div>

            <div className="joinery__seal">
              <strong>Mula Yani</strong>
              <span>Traditional Joinery</span>
            </div>
          </div>

          {/* Content */}
          <div className="joinery__content" data-reveal="right">
            <span className="joinery__eyebrow">Our Craft</span>
            <h2 className="joinery__title">
              Traditional <em>Mula Yani</em> Joinery
              <small>The strength behind true wooden furniture</small>
            </h2>

            <p className="joinery__lead">
              What sets our furniture apart is our commitment to traditional
              craftsmanship.
            </p>

            <p>
              We use <mark>Mula Yani joinery</mark>, a time-tested wood-joining
              technique that brings together carefully selected timber with
              precision and skill. Unlike furniture that depends heavily on
              modern fasteners, this traditional approach focuses on creating
              strong, precise and durable joints within the wood itself.
            </p>

            <p>
              Every joint is carefully crafted to ensure{" "}
              <mark>strength, stability and long-lasting durability</mark>,
              while preserving the natural character and beauty of the timber.
              This meticulous workmanship reflects our belief that truly
              exceptional wooden furniture is not just built. It is{" "}
              <mark>crafted to last for generations</mark>.
            </p>
          </div>
        </div>

        {/* Tagline band */}
        <div className="joinery__tagline" data-reveal="zoom">
          <span>Traditional craftsmanship.</span>
          <span>Natural strength.</span>
          <span>Furniture made to last.</span>
        </div>
      </div>
    </section>
  );
}

const FROM = {
  up: { y: 70, opacity: 0 },
  left: { x: -90, opacity: 0 },
  right: { x: 90, opacity: 0 },
  zoom: { scale: 0.88, opacity: 0 },
};

export default function Home() {
  const root = useRef(null);
  useSmoothScroll();

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Progress bar (tape measure)
        gsap.to(".scroll-progress", {
          scaleX: 1,
          ease: "none",
          scrollTrigger: { start: 0, end: "max", scrub: 0.3 },
        });

        // Generic reveals
        gsap.utils.toArray("[data-reveal]").forEach((el) => {
          gsap.from(el, {
            ...FROM[el.dataset.reveal || "up"],
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%", once: true },
          });
        });

        // Staggered groups
        gsap.utils.toArray("[data-stagger]").forEach((group) => {
          gsap.from(group.children, {
            y: 90,
            opacity: 0,
            rotateX: -18,
            transformOrigin: "50% 100%",
            duration: 1,
            ease: "power3.out",
            stagger: 0.12,
            clearProps: "transform,opacity",
            scrollTrigger: { trigger: group, start: "top 90%", once: true },
          });
        });

        // Clip-path image reveal with counter-scaling
        gsap.utils.toArray(".fx-clip").forEach((el) => {
          gsap.fromTo(
            el,
            { clipPath: "inset(12% 12% 12% 12% round 24px)", scale: 0.92 },
            {
              clipPath: "inset(0% 0% 0% 0% round 14px)",
              scale: 1,
              ease: "none",
              scrollTrigger: {
                trigger: el,
                start: "top 90%",
                end: "top 35%",
                scrub: true,
              },
            },
          );
        });

        // Parallax (images, columns, words)
        gsap.utils.toArray("[data-parallax]").forEach((el) => {
          const s = parseFloat(el.dataset.parallax);
          gsap.fromTo(
            el,
            { yPercent: -s * 100 },
            {
              yPercent: s * 100,
              ease: "none",
              scrollTrigger: {
                trigger: el.parentElement,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            },
          );
        });

        // Horizontal drift text
        gsap.utils.toArray("[data-drift]").forEach((el) => {
          gsap.fromTo(
            el,
            { xPercent: 0 },
            {
              xPercent: parseFloat(el.dataset.drift),
              ease: "none",
              scrollTrigger: {
                trigger: el,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            },
          );
        });

        // Marquee speeds up with scroll
        gsap.to(".marquee__track", {
          xPercent: -25,
          ease: "none",
          scrollTrigger: {
            trigger: ".marquee",
            start: "top bottom",
            end: "bottom top",
            scrub: 0.6,
          },
        });

        // Dark section: background shift
        gsap.fromTo(
          ".section--dark",
          { backgroundPositionY: "0%" },
          {
            backgroundPositionY: "100%",
            ease: "none",
            scrollTrigger: {
              trigger: ".section--dark",
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          },
        );

        // Timeline steps cascade
        gsap.from(".timeline-step", {
          y: 60,
          opacity: 0,
          scale: 0.9,
          duration: 0.9,
          stagger: 0.18,
          ease: "back.out(1.4)",
          scrollTrigger: { trigger: ".timeline", start: "top 80%", once: true },
        });

        // Section title sweep
        gsap.utils.toArray(".section-title").forEach((t) => {
          gsap.from(t.children, {
            y: 40,
            opacity: 0,
            duration: 0.9,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: { trigger: t, start: "top 88%", once: true },
          });
        });

        /* ---------- Furniture-themed effects ---------- */

        // Dovetail joints draw themselves
        gsap.utils.toArray(".dovetail path").forEach((p) => {
          const len = p.getTotalLength();
          gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
          gsap.to(p, {
            strokeDashoffset: 0,
            duration: 2.2,
            ease: "power2.inOut",
            scrollTrigger: { trigger: p, start: "top 95%", once: true },
          });
        });

        // Tree rings slowly rotate as you scroll
        gsap.utils.toArray("[data-spin]").forEach((el) => {
          gsap.to(el, {
            rotation: parseFloat(el.dataset.spin),
            ease: "none",
            scrollTrigger: {
              trigger: el.parentElement,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          });
        });

        // Furniture line-art drifts at different speeds (depth)
        gsap.utils.toArray(".decor-float").forEach((el, i) => {
          const dir = i % 2 ? 1 : -1;
          gsap.fromTo(
            el,
            { y: 70 * dir },
            {
              y: -70 * dir,
              ease: "none",
              scrollTrigger: {
                trigger: el.closest("section") || el.parentElement,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            },
          );
        });

        // Service cards: wood plank "planed" wipe
        ScrollTrigger.batch(".fx-service", {
          start: "top 88%",
          once: true,
          onEnter: (els) =>
            els.forEach((el, i) =>
              gsap.delayedCall(i * 0.12, () => el.classList.add("is-planed")),
            ),
        });
      });

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener("load", refresh);
      return () => window.removeEventListener("load", refresh);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={root}>
      <div className="scroll-progress" aria-hidden="true" />

      <div className="hero-wrap">
        <Hero />
        <PendulumLamp />
      </div>
      <Marquee />

      <section className="section fx-section decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <FloatingFurniture items={SET_A} />
        <div className="container">
          <SectionTitle
            eyebrow="Why Anappuram"
            title="Why Choose Us"
            description="Three decades of dependable, handcrafted furniture built for Kerala homes."
            center
          />
          <div className="feature-grid" data-stagger>
            {FEATURES.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </section>

      <DovetailDivider />

      <JoinerySection />

      <DovetailDivider />

      <AboutPreview />

      <section
        className="section fx-section fx-services decor-host"
        style={{ backgroundColor: "#f2ece1" }}
      >
        <div className="decor-grain" aria-hidden="true" />
        <span className="fx-bigword" data-drift="-18" aria-hidden="true">
          Handcrafted · Handcrafted
        </span>
        <FloatingFurniture items={SET_B} />
        <div className="container">
          <SectionTitle
            eyebrow="What We Build"
            title="Our Services"
            description="Custom wooden furniture crafted for every room, built to your exact measurements."
            center
          />
          <div className="services-grid" data-stagger>
            {SERVICES.map((service) => (
              <div className="fx-service" key={service.key}>
                <ServiceCard
                  image={service.image}
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  delay={0}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <DovetailDivider flip />

      <section className="section section--dark reach decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <Sawdust count={20} />
        <WoodRings className="wood-rings--left" />

        <div className="container reach__grid" data-reveal="up">
          <div className="reach__content">
            <SectionTitle
              eyebrow={REACH.eyebrow}
              title={REACH.title}
              description={REACH.description}
            />
            <div className="btn-row">
              <Button to="/contact">Start Your Order</Button>
              <a
                className="btn btn--outline"
                href={SITE.whatsappLink}
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp Us
              </a>
            </div>
          </div>

          <IndiaReachMap />
        </div>
      </section>

      <section className="section fx-section decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <FloatingFurniture items={SET_A} />
        <div className="container">
          <SectionTitle
            eyebrow="Our Work"
            title="Featured Projects"
            description="A look at recent furniture built and installed for our customers."
            center
          />
          <div className="gallery-grid fx-gallery">
            {GALLERY_ITEMS.map((item, i) => (
              <div
                className="fx-gallery__item"
                key={item.title}
                data-parallax={[0.06, -0.05, 0.1][i % 3]}
              >
                <GalleryCard {...item} delay={0} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--dark decor-host">
        <div className="decor-blueprint" aria-hidden="true" />
        <div className="decor-measure" aria-hidden="true" />
        <FloatingFurniture items={SET_B} />
        <div className="container">
          <SectionTitle
            eyebrow="How We Work"
            title="Our Process"
            description="From first conversation to final installation, in four clear stages."
            center
          />
          <Timeline steps={PROCESS_STEPS} />
        </div>
      </section>

      <section className="section fx-section decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <WoodRings />
        <div className="container" data-reveal="zoom">
          <SectionTitle
            eyebrow="Customer Stories"
            title="What Our Customers Say"
            center
          />
          <TestimonialSlider testimonials={TESTIMONIALS} />
        </div>
      </section>

      <section className="section section--dark stats-section decor-host">
        <Sawdust count={16} />
        <div className="container" data-reveal="up">
          <Stats />
        </div>
      </section>

      <section className="section">
        <CTA />
      </section>
    </div>
  );
}
